import { useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useCurrency } from '../currency/CurrencyProvider';
import {
  useBancos,
  useCobranzasFin,
  useCompras,
  useConciliar,
  useCrearCuentaBancaria,
  useCuentasContables,
  useFacturasFinanzas,
  useMovimientosBancarios,
  usePagosProveedor,
  useTransaccionBancaria,
} from '../api/queries-finanzas';
import { FinanceDemoNotice } from '../components/FinanceBits';
import { Modal } from '../components/Modal';
import { SearchSelect } from '../components/SearchSelect';
import { PlusIcon } from '../components/icons';
import { useToast } from '../ui/ToastProvider';
import { formatDate, formatMoney, todayIso } from '../lib/format';
import type { MovimientoBancario } from '../api/types';

const today = todayIso();

export function BancaPage() {
  const { user, demo } = useAuth();
  const { formatMoney: displayMoney } = useCurrency();
  const { notify } = useToast();
  const [cuentaId, setCuentaId] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [openCuenta, setOpenCuenta] = useState(false);
  const [openTransaccion, setOpenTransaccion] = useState(false);
  const [conciliarMov, setConciliarMov] = useState<MovimientoBancario | null>(null);
  const cuentas = useBancos();
  const movimientos = useMovimientosBancarios({
    cuentaId: cuentaId || undefined,
    desde: desde || undefined,
    hasta: hasta || undefined,
    limit: 100,
  });
  const cuentasContables = useCuentasContables();
  const cobranzasDocs = useCobranzasFin({ limit: 100 });
  const pagosDocs = usePagosProveedor();
  const facturasDocs = useFacturasFinanzas({ limit: 100 });
  const comprasDocs = useCompras({ limit: 100 });
  const crearCuenta = useCrearCuentaBancaria();
  const transaccion = useTransaccionBancaria();
  const conciliar = useConciliar();
  const canEdit = !demo && user !== null && ['admin', 'supervisor'].includes(user.rol);
  const banks = cuentas.data ?? [];
  const rows = movimientos.data?.data ?? [];
  const totalUsd = banks.reduce((sum, bank) => sum + (bank.moneda === 'USD' ? bank.saldoActual : 0), 0);
  const totalVes = banks.reduce((sum, bank) => sum + (bank.moneda === 'VES' ? bank.saldoActual : 0), 0);

  const [cuentaForm, setCuentaForm] = useState({ banco: '', numeroCuenta: '', moneda: 'USD', cuentaContableId: '' });
  const [txForm, setTxForm] = useState({
    cuentaId: '',
    tipo: 'DEPOSITO',
    monto: '',
    moneda: 'USD',
    tasa: '36.42',
    fecha: today,
    referencia: '',
    descripcion: '',
    contraCuentaId: '',
  });
  const [recForm, setRecForm] = useState({ documentoTipo: 'COBRANZA', documentoId: '', montoUsd: '' });

  const documentos = useMemo(() => {
    const format = (monto: number) => formatMoney(monto, 'USD');
    if (recForm.documentoTipo === 'COBRANZA') {
      return (cobranzasDocs.data?.data ?? []).map((item) => ({
        id: item.id,
        label: `${item.numero} · ${item.clienteNombre} · ${format(item.montoUsd)}`,
      }));
    }
    if (recForm.documentoTipo === 'PAGO_PROVEEDOR') {
      return (pagosDocs.data ?? [])
        .filter((item) => item.estado === 'REGISTRADO')
        .map((item) => ({
          id: item.id,
          label: `${item.numero} · ${item.proveedorNombre} · ${format(item.montoUsd)}`,
        }));
    }
    if (recForm.documentoTipo === 'FACTURA') {
      return (facturasDocs.data?.data ?? [])
        .filter((item) => item.estado !== 'anulada')
        .map((item) => ({
          id: item.id,
          label: `${item.numero} · ${item.clienteNombre} · ${format(item.totalUsd)}`,
        }));
    }
    return (comprasDocs.data?.data ?? [])
      .filter((item) => item.estado !== 'ANULADA')
      .map((item) => ({
        id: item.id,
        label: `${item.numeroInterno} · ${item.proveedorNombre} · ${format(item.totalUsd)}`,
      }));
  }, [recForm.documentoTipo, cobranzasDocs.data, pagosDocs.data, facturasDocs.data, comprasDocs.data]);

  const submitCuenta = async () => {
    if (!cuentaForm.banco.trim() || !cuentaForm.numeroCuenta.trim()) {
      notify('Banco y número de cuenta son obligatorios.', 'warning');
      return;
    }
    try {
      await crearCuenta.mutateAsync({
        banco: cuentaForm.banco.trim(),
        numeroCuenta: cuentaForm.numeroCuenta.trim(),
        moneda: cuentaForm.moneda,
        ...(cuentaForm.cuentaContableId ? { cuentaContableId: cuentaForm.cuentaContableId } : {}),
      });
      notify('Cuenta bancaria creada.', 'success');
      setOpenCuenta(false);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear la cuenta.', 'error');
    }
  };

  const submitTx = async () => {
    if (!txForm.cuentaId || !txForm.contraCuentaId || !(Number(txForm.monto) > 0)) {
      notify('Cuenta, contrapartida y monto son obligatorios.', 'warning');
      return;
    }
    const tasa = Number(txForm.tasa) || 1;
    const montoUsd =
      txForm.moneda === 'USD'
        ? Number(txForm.monto)
        : Math.round((Number(txForm.monto) / tasa) * 10000) / 10000;
    try {
      await transaccion.mutateAsync({
        cuentaId: txForm.cuentaId,
        tipo: txForm.tipo,
        monto: Number(txForm.monto),
        moneda: txForm.moneda,
        tasa,
        fecha: txForm.fecha,
        ...(txForm.referencia ? { referencia: txForm.referencia } : {}),
        ...(txForm.descripcion ? { descripcion: txForm.descripcion } : {}),
        lineas: [{ cuentaId: txForm.contraCuentaId, montoUsd }],
      });
      notify('Transacción bancaria registrada y contabilizada.', 'success');
      setOpenTransaccion(false);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo registrar la transacción.', 'error');
    }
  };

  const submitConciliacion = async () => {
    if (!conciliarMov || !recForm.documentoId || !(Number(recForm.montoUsd) > 0)) {
      notify('Documento y monto son obligatorios.', 'warning');
      return;
    }
    try {
      await conciliar.mutateAsync({
        movimientoId: conciliarMov.id,
        documentoTipo: recForm.documentoTipo,
        documentoId: recForm.documentoId,
        montoUsd: Number(recForm.montoUsd) / (conciliarMov.tasa || 1),
      });
      notify('Movimiento conciliado.', 'success');
      setConciliarMov(null);
      setRecForm({ documentoTipo: 'COBRANZA', documentoId: '', montoUsd: '' });
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo conciliar.', 'error');
    }
  };

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Tesorería</p>
          <h1 className="page-title">Banca & Conciliación</h1>
          <p className="page-sub">Cuentas, depósitos, retiros, transacciones directas y conciliación documental.</p>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--outline" disabled={!canEdit} onClick={() => setOpenCuenta(true)}>
            Nueva cuenta
          </button>
          <button type="button" className="btn btn--primary" disabled={!canEdit} onClick={() => setOpenTransaccion(true)}>
            <PlusIcon size={17} /> Transacción
          </button>
        </div>
      </header>

      <FinanceDemoNotice demo={demo} />

      <div className="summary-strip">
        <div className="summary-tile"><span>Bancos (Bs.)</span><strong>{displayMoney(totalUsd)}</strong><small>{banks.filter((b) => b.moneda === 'USD').length} cuentas</small></div>
        <div className="summary-tile"><span>Bancos en Bs.</span><strong>{formatMoney(totalVes, 'VES')}</strong><small>{banks.filter((b) => b.moneda === 'VES').length} cuentas</small></div>
        <div className="summary-tile summary-tile--accent"><span>Movimientos</span><strong>{movimientos.data?.meta.total ?? rows.length}</strong><small>En el rango</small></div>
      </div>

      <div className="bank-grid">
        {banks.map((bank) => (
          <article className="card bank-card" key={bank.id}>
            <div className="bank-card__head">
              <div>
                <strong>{bank.banco}</strong>
                <span>{bank.numeroCuenta}</span>
              </div>
              <span className="pill pill--soft">{bank.moneda}</span>
            </div>
            <div className="bank-card__value">{formatMoney(bank.saldoActual, bank.moneda)}</div>
            <p className="bank-card__hint">
              {bank.cuentaCodigo ? `Cuenta ${bank.cuentaCodigo} · ${bank.cuentaNombre}` : 'Sin cuenta contable vinculada'}
            </p>
          </article>
        ))}
        {banks.length === 0 ? <div className="card bank-card"><p className="muted">Sin cuentas bancarias. Crea la primera.</p></div> : null}
      </div>

      <section className="card transactions-card">
        <div className="transactions-filters">
          <div className="filter-tabs">
            <button type="button" className={cuentaId === '' ? 'is-active' : undefined} onClick={() => setCuentaId('')}>Todas las cuentas</button>
            {banks.map((bank) => (
              <button key={bank.id} type="button" className={cuentaId === bank.id ? 'is-active' : undefined} onClick={() => setCuentaId(bank.id)}>
                {bank.banco}
              </button>
            ))}
          </div>
          <div className="filter-controls">
            <input type="date" className="input input--date" value={desde} onChange={(e) => setDesde(e.target.value)} aria-label="Desde" />
            <input type="date" className="input input--date" value={hasta} onChange={(e) => setHasta(e.target.value)} aria-label="Hasta" />
          </div>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cuenta</th>
                <th>Tipo</th>
                <th>Origen</th>
                <th>Referencia</th>
                <th className="align-right">Monto</th>
                <th>Conciliado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {movimientos.isLoading ? <tr><td colSpan={8}><div className="table-empty">Cargando movimientos…</div></td></tr> : null}
              {!movimientos.isLoading && rows.length === 0 ? <tr><td colSpan={8}><div className="table-empty">Sin movimientos bancarios.</div></td></tr> : null}
              {rows.map((movement) => (
                <tr key={movement.id}>
                  <td>{formatDate(movement.fecha)}</td>
                  <td>
                    <div className="doc-cell">
                      <strong>{movement.banco}</strong>
                      <span>{movement.numeroCuenta}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge status-badge--${movement.tipo === 'DEPOSITO' ? 'success' : 'warning'}`}>
                      {movement.tipo}
                    </span>
                  </td>
                  <td>{movement.origen}</td>
                  <td>{movement.referencia ?? movement.descripcion ?? '—'}</td>
                  <td className="align-right">
                    <div className="amount-cell">
                      <strong>{formatMoney(movement.montoVes, 'VES')}</strong>
                      <span>{movement.tipo === 'DEPOSITO' ? 'Entrada' : 'Salida'}</span>
                    </div>
                  </td>
                  <td>{movement.conciliado ? <span className="saldo-ok">Sí</span> : <span className="saldo-pending">Pendiente</span>}</td>
                  <td>
                    {!movement.conciliado ? (
                      <button
                        type="button"
                        className="btn btn--outline btn--sm"
                        onClick={() => {
                          setConciliarMov(movement);
                          setRecForm({
                            documentoTipo:
                              movement.origen === 'COBRANZA'
                                ? 'COBRANZA'
                                : movement.origen === 'PAGO_PROVEEDOR'
                                  ? 'PAGO_PROVEEDOR'
                                  : movement.tipo === 'DEPOSITO'
                                    ? 'COBRANZA'
                                    : 'PAGO_PROVEEDOR',
                            documentoId: movement.origenId ?? '',
                            montoUsd: String(movement.montoUsd),
                          });
                        }}
                      >
                        Conciliar
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {openCuenta ? (
        <Modal title="Nueva cuenta bancaria" eyebrow="Tesorería" onClose={() => setOpenCuenta(false)} footer={
          <>
            <button type="button" className="btn btn--outline" onClick={() => setOpenCuenta(false)}>Cancelar</button>
            <button type="button" className="btn btn--primary" onClick={() => void submitCuenta()} disabled={crearCuenta.isPending}>Crear cuenta</button>
          </>
        }>
          <div className="modal-card__body">
            <div className="form-grid">
              <label className="field"><span className="field-label">Banco *</span><input className="input" value={cuentaForm.banco} onChange={(e) => setCuentaForm({ ...cuentaForm, banco: e.target.value })} /></label>
              <label className="field"><span className="field-label">Número de cuenta *</span><input className="input" value={cuentaForm.numeroCuenta} onChange={(e) => setCuentaForm({ ...cuentaForm, numeroCuenta: e.target.value })} /></label>
              <label className="field"><span className="field-label">Moneda</span><select className="input" value={cuentaForm.moneda} onChange={(e) => setCuentaForm({ ...cuentaForm, moneda: e.target.value })}><option value="USD">USD</option><option value="VES">VES</option></select></label>
              <label className="field"><span className="field-label">Cuenta contable</span><SearchSelect
                value={cuentaForm.cuentaContableId}
                onChange={(value) => setCuentaForm({ ...cuentaForm, cuentaContableId: value })}
                options={[
                  { value: '', label: 'Usar mapeo por moneda' },
                  ...(cuentasContables.data ?? [])
                    .filter((c) => c.tipo === 'ACTIVO' && c.esDetalle)
                    .map((c) => ({ value: c.id, label: c.nombre, hint: c.codigo })),
                ]}
                placeholder="Cuenta contable"
                ariaLabel="Cuenta contable"
              /></label>
            </div>
          </div>
        </Modal>
      ) : null}

      {openTransaccion ? (
        <Modal title="Transacción bancaria directa" eyebrow="Tesorería" onClose={() => setOpenTransaccion(false)} wide footer={
          <>
            <button type="button" className="btn btn--outline" onClick={() => setOpenTransaccion(false)}>Cancelar</button>
            <button type="button" className="btn btn--primary" onClick={() => void submitTx()} disabled={transaccion.isPending}>Registrar y contabilizar</button>
          </>
        }>
          <div className="modal-card__body">
            <div className="form-grid form-grid--3">
              <label className="field"><span className="field-label">Cuenta bancaria *</span><SearchSelect
                value={txForm.cuentaId}
                onChange={(value) => setTxForm({ ...txForm, cuentaId: value })}
                options={banks.map((bank) => ({
                  value: bank.id,
                  label: bank.banco,
                  hint: `${bank.moneda} · ${bank.numeroCuenta ?? ''}`.trim(),
                }))}
                placeholder="Selecciona la cuenta bancaria"
                ariaLabel="Cuenta bancaria"
              /></label>
              <label className="field"><span className="field-label">Tipo</span><select className="input" value={txForm.tipo} onChange={(e) => setTxForm({ ...txForm, tipo: e.target.value })}><option value="DEPOSITO">Depósito</option><option value="RETIRO">Retiro</option></select></label>
              <label className="field"><span className="field-label">Fecha</span><input className="input" type="date" value={txForm.fecha} onChange={(e) => setTxForm({ ...txForm, fecha: e.target.value })} /></label>
              <label className="field"><span className="field-label">Moneda</span><select className="input" value={txForm.moneda} onChange={(e) => setTxForm({ ...txForm, moneda: e.target.value })}><option value="USD">USD</option><option value="VES">VES</option></select></label>
              <label className="field"><span className="field-label">Monto *</span><input className="input" inputMode="decimal" value={txForm.monto} onChange={(e) => setTxForm({ ...txForm, monto: e.target.value })} /></label>
              <label className="field"><span className="field-label">Tasa VES/USD</span><input className="input" value={txForm.tasa} onChange={(e) => setTxForm({ ...txForm, tasa: e.target.value })} /></label>
              <label className="field field--wide"><span className="field-label">Contrapartida contable *</span><SearchSelect
                value={txForm.contraCuentaId}
                onChange={(value) => setTxForm({ ...txForm, contraCuentaId: value })}
                options={(cuentasContables.data ?? [])
                  .filter((c) => c.esDetalle)
                  .map((c) => ({ value: c.id, label: c.nombre, hint: c.codigo }))}
                placeholder="Buscar cuenta contable"
                ariaLabel="Contrapartida contable"
              /></label>
              <label className="field"><span className="field-label">Referencia</span><input className="input" value={txForm.referencia} onChange={(e) => setTxForm({ ...txForm, referencia: e.target.value })} /></label>
              <label className="field field--wide"><span className="field-label">Descripción</span><input className="input" value={txForm.descripcion} onChange={(e) => setTxForm({ ...txForm, descripcion: e.target.value })} /></label>
            </div>
          </div>
        </Modal>
      ) : null}

      {conciliarMov ? (
        <Modal title="Conciliar movimiento" eyebrow={`${conciliarMov.tipo} · ${formatMoney(conciliarMov.monto, conciliarMov.moneda)}`} onClose={() => setConciliarMov(null)} footer={
          <>
            <button type="button" className="btn btn--outline" onClick={() => setConciliarMov(null)}>Cancelar</button>
            <button type="button" className="btn btn--primary" onClick={() => void submitConciliacion()} disabled={conciliar.isPending}>Conciliar</button>
          </>
        }>
          <div className="modal-card__body">
            <div className="form-grid">
              <label className="field"><span className="field-label">Tipo de documento</span><select className="input" value={recForm.documentoTipo} onChange={(e) => setRecForm({ ...recForm, documentoTipo: e.target.value, documentoId: '' })}><option value="COBRANZA">Cobranza</option><option value="PAGO_PROVEEDOR">Pago proveedor</option><option value="FACTURA">Factura</option><option value="COMPRA">Compra</option></select></label>
              <label className="field"><span className="field-label">Documento *</span><SearchSelect
                value={recForm.documentoId}
                onChange={(value) => setRecForm({ ...recForm, documentoId: value })}
                options={documentos.map((doc) => ({ value: doc.id, label: doc.label }))}
                placeholder="Buscar documento"
                ariaLabel="Documento a conciliar"
              /></label>
              <label className="field"><span className="field-label">Monto Bs. *</span><input className="input" inputMode="decimal" value={recForm.montoUsd} onChange={(e) => setRecForm({ ...recForm, montoUsd: e.target.value })} /></label>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
